import { thirdPartyConnectedDataSourceResource } from '@jira/router-resources__third-party-connected-data-source/src/thirdPartyConnectedDataSourceResource.tsx';
import { IssueViewSkeleton } from '@jira/issue__issue-view-skeleton/src/IssueViewSkeleton.tsx';
import { currentUserResource } from '@jira/router-resources__current-user/src/useCurrentUserResource.tsx';
import { loomPermissionResource } from '@jira/router-resources__loom/src/loomPermissionResource.tsx';
import { confluenceUserPermissionsResource } from '@jira/router-resources__confluence-user-permissions/src/confluenceUserPermissionsResource.tsx';
import { isJsmChatOpsEntitlementResourceEnabled } from '@jira/router-resources__jsm-chat-ops/src/isJsmChatOpsEntitlementResourceEnabled.tsx';
import { jsmChatOpsEntitlementResource } from '@jira/router-resources__jsm-chat-ops/src/useJsmChatOpsEntitlementResource.tsx';
import { getAIContextPanelResources } from '@jira/router-resources__service-desk-ai-context-panel/src/getAIContextPanelResources.tsx';
import { getResolutionPlanSSRResource } from '@jira/router-resources__service-desk-ai-context-panel/src/ResolutionPlanSSRResource.tsx';
import { workspaceResource } from '@jira/servicedesk__insight-common-services-workspace-context/src/workspaceResource.tsx';
import { ISSUE_VIEW } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { chromelessLayout } from '@jira/platform__layouts-chromeless-layout/src/index.tsx';
import { NORMAL_EARLY_ASSET_STRATEGY } from '@jira/platform__common-constants/src/ssr-early-assets.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { issueViewRelayResource } from '@jira/router-resources__issue-view/src/useIssueViewRelayRouteResource.tsx';
import { issueGiraDataResource } from '@jira/router-resources__issue-view/src/issueGiraDataResource.tsx';
import { getModernIssueTransitionResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { jiraSpaResources } from '@jira/router-resources__spa-page-container/src/jiraSpaResources.tsx';
import { browseIssueEmbedRoute } from '@jira/router-routes__issue-routes/src/browseIssueEmbedRoute.tsx';
import { getDraftReplyPillsResource } from '@jira/router-resources__service-desk-draft-reply/src/getDraftReplyPillsResource.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { projectGlobalComponentsResource } from '@jira/router-resources__project-global-components/src/projectGlobalComponentsResource.tsx';
import {
	getJsmIssueViewRelatedArticlesRouteResource,
	getJsmIssueViewSlaPanelResource,
	getJsmIssueViewPanelMetadataResource,
} from '@jira/router-resources__jsm-issue-view/src/getJsmIssueViewRelatedArticlesRouteResource.tsx';
import { jsmMIMEntitlementResource } from '@jira/router-resources__jsm-major-incident-entitlement/src/useJsmMimEntitlementResource.tsx';
import { issueCollapsibleGroupDataResource } from '@jira/router-resources__issue-view/src/issueCollapsibleGroupDataResource.tsx';
import { issueViewRemoteDataResource } from '@jira/router-resources__issue-view/src/issueViewRemoteDataResource.tsx';
import { issueViewResourcesDataResource } from '@jira/router-resources__issue-view/src/issueViewResourcesDataResource.tsx';
import { issueSelectedTabDataResource } from '@jira/router-resources__issue-view/src/issueSelectedTabDataResource.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { issueAttachmentsSmartlinkDataResource } from '@jira/router-resources__issue-view/src/issueAttachmentsSmartlinkDataResource.tsx';
import { issueDescriptionSmartlinkDataResource } from '@jira/router-resources__issue-view/src/issueDescriptionSmartlinkDataResource.tsx';
import type { BifrostRedirectHandlerPayload } from '@jira/platform__routing-route-types/src/JiraSpaRoute.tsx';
import { LazyIssuePage, IssueEmbedComponent } from './ui/index.tsx';
import { bifrostBrowseRouteRedirectionHandler } from './common/bifrostBrowseRouteRedirectionHandler.tsx';
import type { Route } from '@atlassian/react-resource-router';

export const browseIssueEmbedRouteEntry: Route = createEntry(browseIssueEmbedRoute, () => ({
	isStrictModeEnabled: fg('kenji-jira-incremental-strict-mode'),
	group: 'issue',
	preloadOptions: {
		earlyChunks: ['issue-app-early-entry'],
	},
	analyticsOptions: {
		// CMON-3192 [POC]: emit a SignalFx counter at SSR time for the viewIssue
		// experience (embed variant). Same experience name as the standalone route
		// so CMON aggregates both under "viewIssue".
		// Gate: jfp-cmon-task-start-issue-view controls emission at call site (multi-spa-route.tsx).
		taskStart: { experience: 'viewIssue' },
	},
	earlyAssets: NORMAL_EARLY_ASSET_STRATEGY,
	// eslint-disable-next-line no-restricted-syntax
	component: IssueEmbedComponent,
	skeleton: IssueViewSkeleton,
	layout: chromelessLayout,
	resources: [
		...getModernIssueTransitionResources(),
		resourceWithCondition(() => !fg('remove-issueviewinteractivequery-m4'), issueGiraDataResource),
		...getIssueViewResources(),
		issueViewRelayResource,
		workspaceResource,
		...jiraSpaResources(),
		resourceWithCondition(
			() => fg('fix_subproduct_on_issue_embed_preview_panel'),
			// eslint-disable-next-line jira/router/no-project-context-resource
			projectContextResource,
		),
		...getAIContextPanelResources(),
		...getResolutionPlanSSRResource(),
		...getDraftReplyPillsResource(),
		projectGlobalComponentsResource,
		getJsmIssueViewSlaPanelResource(),
		resourceWithCondition(
			() => fg('jsm_flash_ssr_major_incident_management_fields'),
			jsmMIMEntitlementResource,
		),
		getJsmIssueViewRelatedArticlesRouteResource(),
		getJsmIssueViewPanelMetadataResource(),
		issueSelectedTabDataResource,
		resourceWithCondition(
			() => fg('jfp-magma-ssr-iv-collapse-group'),
			issueCollapsibleGroupDataResource,
		),
		issueViewRemoteDataResource,
		resourceWithCondition(
			() => expVal('attachments-1p-consolidation-experiment', 'isExperimentEnabled', false),
			issueViewResourcesDataResource,
		),
		resourceWithCondition(
			() => fg('jira-issue-view-3p-attachments'), // additional FG that need 3P resources should use or condition
			thirdPartyConnectedDataSourceResource,
		),
		currentUserResource,
		loomPermissionResource,
		resourceWithCondition(
			() => fg('jpx-833-smart-links-graphql-provider'),
			issueDescriptionSmartlinkDataResource,
		),
		resourceWithCondition(
			() =>
				// eslint-disable-next-line jira/ff/no-preconditioning
				fg('attachments_consolidation_smart_links_ssr') &&
				expVal('attachments-1p-consolidation-experiment', 'isExperimentEnabled', false),
			issueAttachmentsSmartlinkDataResource,
		),
		resourceWithCondition(
			() => fg('fg-perf-issue-view-confluence-menu-optimisation'),
			confluenceUserPermissionsResource,
		),
		resourceWithCondition(isJsmChatOpsEntitlementResourceEnabled, jsmChatOpsEntitlementResource),
	],
	forPaint: [LazyIssuePage],
	ufoName: 'issue-view-embed',
	fallbackExperience: 'viewIssue',
	meta: {
		capability: ISSUE_VIEW,
	},
	bifrost: {
		bifrostGroup: 'issue-view',
		manifestName: 'jira-spa-issue-view.json',
		redirects: [
			{
				key: 'issue-project-context-redirect',
				timeoutMs: 1000,
				handler: ({ match, url, tenantContext }: BifrostRedirectHandlerPayload) =>
					bifrostBrowseRouteRedirectionHandler({ match, url, tenantContext, isEmbed: true }),
			},
		],
	},
	supportsPreviewPanel: true,
	isHydrateRootEnabled: true,
	isStreamingEnabled: fg('jfp-magma-ssr-streaming-enable-iv-embed'),
	excludeFromWrmDeferredLoading: true,
}));
